import React, { useState, useEffect, useMemo } from 'react';
import OfficeService from './officeService';
import imageService from '../images/imageService';
import './OfficeDisplay.css';

const OfficeDisplay = () => {
  const userRole = localStorage.getItem('role');
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeImages, setOfficeImages] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedOffice, setEditedOffice] = useState({});
  const [uploading, setUploading] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(null);

  const officeService = useMemo(() => new OfficeService(), []);
  const imageDir = "office";

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const data = await officeService.getAllOffices();
        setOffices(data);
      } catch (err) {
        console.error("Failed to fetch offices:", err);
      }
    };
    fetchOffices();
  }, [officeService]);

  const openPopup = (office) => {
    setSelectedOffice(office);
    setEditedOffice({ ...office });

    const images = office.officeImage
      ? office.officeImage.split(',').filter(img => img.trim() !== "")
      : [];
    setOfficeImages(images);

    setEditMode(false);
  };

  const closePopup = () => {
    setSelectedOffice(null);
    setEditMode(false);
    setOfficeImages([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedOffice((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedOffice?.officeName) {
      alert("No file selected or office not chosen.");
      return;
    }

    setUploading(true);
    const uploaded = [];

    for (const file of files) {
      if (!file || file.size === 0) continue;

      try {
        await imageService.uploadImage(file, file.name, imageDir);
        uploaded.push(file.name);
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }

    const updatedImageList = [...officeImages, ...uploaded];
    setOfficeImages(updatedImageList);

    try {
      await officeService.updateOfficeImages(
        selectedOffice.officeId,
        updatedImageList
      );
    } catch (err) {
      console.error("Failed to save image list to DB:", err);
    }

    setUploading(false);
  };

  const deleteImage = async (filenameToRemove) => {
    const updatedList = officeImages.filter((img) => img !== filenameToRemove);
    setOfficeImages(updatedList);

    try {
      await officeService.updateOfficeImages(selectedOffice.officeId, updatedList);
      await imageService.deleteImage(imageDir, filenameToRemove);
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  const saveOfficeChanges = async () => {
    try {
      await officeService.updateOffice(selectedOffice.officeId, editedOffice);
      alert("Office info updated");
      setEditMode(false);
      setSelectedOffice({ ...selectedOffice, ...editedOffice });
    } catch (err) {
      alert("Failed to update office info");
      console.error(err);
    }
  };

  const handleLightboxPrev = (e) => {
    e.stopPropagation();
    setLightboxImageIndex((lightboxImageIndex - 1 + officeImages.length) % officeImages.length);
  };

  const handleLightboxNext = (e) => {
    e.stopPropagation();
    setLightboxImageIndex((lightboxImageIndex + 1) % officeImages.length);
  };

  return (
    <div className="office-container">
      {offices.map((office) => (
        <div key={office.officeId} className="office-card">
          <img
            src={imageService.getImageUrl(imageDir, office.officeImage?.split(',')[0])}
            alt={`${office.officeName} preview`}
            className="office-image"
          />
          <h2>{office.officeName}</h2>
          <p>{office.address}</p>
          <button onClick={() => openPopup(office)}>More Details</button>
        </div>
      ))}

      {selectedOffice && (
        <div className="popup-overlay">
          <div className="popup-content">
            <span className="popup-close" onClick={closePopup}>&times;</span>
            <h2>{editMode ? "Edit Office" : selectedOffice.officeName}</h2>

            {editMode ? (
              <>
                <input name="officeName" value={editedOffice.officeName} onChange={handleInputChange} />
                <textarea name="description" value={editedOffice.description} onChange={handleInputChange} />
                <input name="phoneNumber" value={editedOffice.phoneNumber} onChange={handleInputChange} />
                <input name="emial" value={editedOffice.emial} onChange={handleInputChange} />

                <div>
                  <label>Upload Image(s):</label>
                  <input type="file" multiple onChange={handleUpload} disabled={uploading} />
                </div>

                <button onClick={saveOfficeChanges} disabled={uploading}>Save</button>
              </>
            ) : (
              <>
                <p><strong>Address:</strong> {selectedOffice.address}</p>
                <p><strong>Description:</strong> {selectedOffice.description}</p>
                <p><strong>Phone:</strong> {selectedOffice.phoneNumber}</p>
                <p><strong>Email:</strong> {selectedOffice.emial}</p>
              </>
            )}

            {officeImages.length > 0 && (
              <div className="image-grid">
                {officeImages.map((img, index) => (
                  <div key={index} className="grid-img-wrapper">
                    <img
                      src={imageService.getImageUrl(imageDir, img)}
                      alt={`${selectedOffice.officeName} img`}
                      className="grid-img"
                      onClick={() => setLightboxImageIndex(index)}
                    />
                    {editMode && (
                      <button className="delete-img-btn" onClick={() => deleteImage(img)}>🗑</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {['ADMIN', 'MANAGER', 'DOCTOR'].includes(userRole) && !editMode && (
              <button onClick={() => setEditMode(true)}>Edit</button>
            )}
          </div>
        </div>
      )}

      {lightboxImageIndex !== null && (
        <div className="lightbox-overlay" onClick={() => setLightboxImageIndex(null)}>
          <button className="lightbox-nav left" onClick={handleLightboxPrev}>‹</button>
          <img
            src={imageService.getImageUrl(imageDir, officeImages[lightboxImageIndex])}
            className="lightbox-image"
            alt="Lightbox"
          />
          <button className="lightbox-nav right" onClick={handleLightboxNext}>›</button>
        </div>
      )}
    </div>
  );
};

export default OfficeDisplay;
